"use client";

import { useState } from "react";
import Link from "next/link";
import { EmailGate } from "@/components/shared/EmailGate";
import { ShareBar } from "@/components/shared/ShareBar";
import type { QuizDefinition, ResultLevel } from "@/lib/quiz-types";

interface QuizEngineProps {
  quiz: QuizDefinition;
}

export function QuizEngine({ quiz }: QuizEngineProps) {
  const [step, setStep] = useState<"intro" | "questions" | "result">("intro");
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});

  const totalQuestions = quiz.questions.length;
  const progress = step === "result" ? 100 : Math.round((Object.keys(answers).length / totalQuestions) * 100);

  function handleAnswer(value: number) {
    const q = quiz.questions[currentQ];
    setAnswers((prev) => ({ ...prev, [q.id]: value }));
    if (currentQ < totalQuestions - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      setStep("result");
    }
  }

  function calculateScore(): { score: number; level: ResultLevel } {
    let score = 0;
    if (quiz.scoring.method === "sum") {
      score = Object.values(answers).reduce((s, v) => s + v, 0);
    } else if (quiz.scoring.method === "average") {
      const values = Object.values(answers);
      score = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
    }
    const level =
      quiz.resultLevels.find((l) => score >= l.minScore && score <= l.maxScore) ||
      quiz.resultLevels[0];
    return { score, level };
  }

  // ── INTRO ──
  if (step === "intro") {
    return (
      <div className="bg-white border border-stone-200 rounded-3xl p-8 md:p-10">
        <div className="inline-flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-full px-3 py-1 text-xs text-violet-800 mb-4">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {quiz.source}
        </div>
        <h2 className="text-3xl md:text-4xl font-light tracking-tight text-stone-900 mb-4">{quiz.title}</h2>
        <p className="text-lg text-stone-500 font-light max-w-2xl mb-6">{quiz.description}</p>
        <div className="flex flex-wrap gap-4 text-xs text-stone-500 mb-8">
          <span className="flex items-center gap-1.5">⏱ {quiz.estimatedMinutes} min</span>
          <span className="flex items-center gap-1.5">📋 {totalQuestions} spørsmål</span>
          <span className="flex items-center gap-1.5">🔒 Anonym</span>
        </div>
        <button
          onClick={() => setStep("questions")}
          className="bg-stone-900 hover:bg-stone-800 text-white px-8 py-3.5 rounded-full font-medium transition-colors"
        >
          Start testen →
        </button>
        <div className="mt-8 text-xs text-stone-500 leading-relaxed bg-stone-50 rounded-2xl p-4">
          <strong className="text-stone-700">Disclaimer:</strong> {quiz.disclaimer}
        </div>
        <ShareBar label="Del denne testen" />
      </div>
    );
  }

  // ── QUESTIONS ──
  if (step === "questions") {
    const q = quiz.questions[currentQ];
    return (
      <div>
        <div className="mb-8">
          <div className="flex items-center justify-between text-xs text-stone-500 mb-2">
            <span>
              Spørsmål {currentQ + 1} av {totalQuestions}
            </span>
            <span className="font-medium">{progress} %</span>
          </div>
          <div className="w-full h-1.5 bg-stone-200 rounded-full overflow-hidden">
            <div className="h-full bg-stone-900 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-3xl p-8 md:p-10 mb-6">
          {q.helpText && <p className="text-xs uppercase tracking-wider text-stone-400 mb-3">{q.helpText}</p>}
          <h2 className="text-2xl md:text-3xl font-light tracking-tight text-stone-900 mb-8">{q.text}</h2>
          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const selected = answers[q.id] === opt.value;
              return (
                <button
                  key={i}
                  onClick={() => handleAnswer(opt.value)}
                  className={
                    "w-full flex items-start gap-4 p-4 border-2 rounded-2xl transition-colors text-left " +
                    (selected
                      ? "border-stone-900 bg-stone-50"
                      : "border-stone-200 hover:border-stone-400")
                  }
                >
                  <span
                    className={
                      "w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center " +
                      (selected ? "border-stone-900 bg-stone-900" : "border-stone-300")
                    }
                  >
                    {selected && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span className="font-medium text-stone-900">{opt.text}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={() => currentQ > 0 && setCurrentQ(currentQ - 1)}
            disabled={currentQ === 0}
            className="text-sm text-stone-500 hover:text-stone-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
          >
            ← Forrige
          </button>
        </div>

        {quiz.crisisInfo?.show && currentQ === totalQuestions - 1 && (
          <div className="mt-8 bg-red-50 border border-red-300 rounded-2xl p-5 text-sm">
            <p className="font-medium text-red-900 mb-2">{quiz.crisisInfo.text}</p>
            <ul className="text-red-900 space-y-1.5">
              {quiz.crisisInfo.resources.map((r, i) => (
                <li key={i}>
                  📞 <strong>{r.name}</strong>: {r.phone}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // ── RESULT ──
  const { score, level } = calculateScore();

  return (
    <div>
      <div
        className="rounded-3xl p-8 md:p-10 mb-6 border"
        style={{
          background: `linear-gradient(135deg, ${level.color}15 0%, #fafaf9 100%)`,
          borderColor: `${level.color}40`,
        }}
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-3 h-3 rounded-full" style={{ background: level.color }}></div>
          <p className="text-sm font-medium text-stone-700">
            Resultat: Score {Math.round(score)} av {quiz.scoring.maxScore}
          </p>
        </div>
        <h2 className="text-3xl md:text-4xl font-light tracking-tight text-stone-900 mb-4">{level.label}</h2>
        <p className="text-stone-700 leading-relaxed mb-6">{level.description}</p>

        {quiz.scoring.method === "sum" && (
          <div className="bg-white rounded-2xl p-5 mb-6 border border-stone-100">
            <div className="flex justify-between text-xs text-stone-500 mb-2">
              {quiz.resultLevels.map((l, i) => (
                <span key={i}>{l.minScore}</span>
              ))}
              <span>{quiz.scoring.maxScore}</span>
            </div>
            <div
              className="relative h-3 rounded-full"
              style={{
                background: `linear-gradient(90deg, ${quiz.resultLevels.map((l) => l.color).join(", ")})`,
              }}
            >
              <div
                className="absolute -top-1 w-5 h-5 rounded-full bg-white border-2 border-stone-900 shadow-md"
                style={{
                  left: `calc(${(score / quiz.scoring.maxScore) * 100}% - 10px)`,
                }}
              ></div>
            </div>
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-stone-900 mb-2">Anbefaling</h3>
          <p className="text-sm text-stone-700 leading-relaxed">{level.recommendation}</p>
        </div>

        <Link
          href={level.ctaHref}
          className="block w-full text-center bg-stone-900 hover:bg-stone-800 text-white py-3.5 rounded-full font-medium transition-colors"
        >
          {level.ctaText} →
        </Link>
      </div>

      <EmailGate
        toolSlug={quiz.slug}
        toolType="quiz"
        headline={quiz.emailGate.headline}
        description={quiz.emailGate.description}
        buttonText={quiz.emailGate.buttonText}
        resultData={{
          score: Math.round(score),
          level: level.label,
          metadata: {
            kind: "quiz",
            title: quiz.title,
            maxScore: quiz.scoring.maxScore,
            levelLabel: level.label,
            levelDescription: level.description,
            recommendation: level.recommendation,
            ctaText: level.ctaText,
            ctaHref: level.ctaHref,
          },
        }}
      />

      {quiz.crisisInfo?.show && score >= quiz.resultLevels[3]?.minScore && (
        <div className="mt-6 bg-red-50 border border-red-300 rounded-2xl p-5">
          <h4 className="text-sm font-semibold text-red-900 mb-2">Du er ikke alene — hjelp finnes</h4>
          <ul className="text-sm text-red-900 space-y-1.5">
            {quiz.crisisInfo.resources.map((r, i) => (
              <li key={i}>
                📞 <strong>{r.name}</strong>: {r.phone}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ShareBar label="Del denne testen" />
    </div>
  );
}
